import { useMutation } from '@tanstack/react-query';

import { useNotification } from '@sine/sine-ui-components';

import { promiseStatus } from '../../../helpers/async';
import formatSingularPlural from '../../../helpers/formatSingularPlural';
import parseServerError from '../../../helpers/parseServerError';
import { MutationKeys } from '../../../query.client';
import invitationsService from '../../../services/invitation/invitations.service';
import type { InitialInvitationValues, InviteesRef } from '../Invitations.types';
import { composeInvitations } from '../helpers/composeInvitations';

interface UseCreateInvitationsProps {
  onClose: VoidFunction;
}

export function useCreateInvitations({ onClose }: UseCreateInvitationsProps) {
  const { addNotification, clearNotifications } = useNotification();

  let createdPluralSingular: string;

  const { mutateAsync: createInvitations, isLoading: isLoadingCreate } = useMutation({
    mutationKey: [MutationKeys.CREATE_INVITATION],
    mutationFn: async ({
      inviteesFormRef,
      invitationDetails,
      canSendEmail,
    }: {
      inviteesFormRef: InviteesRef;
      invitationDetails: InitialInvitationValues['invitationDetails'];
      canSendEmail: boolean;
    }) => {
      const composedInvitations = await composeInvitations(inviteesFormRef, invitationDetails);

      const invitationsPluralSingular = formatSingularPlural(
        composedInvitations.length,
        'invitation',
        'invitations',
        true,
      );

      addNotification({
        message: `Processing your ${invitationsPluralSingular}`,
        variant: 'info',
      });

      const response = await invitationsService.createMultipleInvitations(composedInvitations, canSendEmail);

      clearNotifications();

      const createdInvitations = response.filter((invitation) => invitation.status !== promiseStatus.rejected);
      const blockedInvitations = response.filter((invitation) => invitation.status === promiseStatus.rejected);

      createdPluralSingular = formatSingularPlural(createdInvitations.length, 'invitation', 'invitations', true);

      if (createdInvitations.length >= 1) {
        addNotification({
          heading: `${createdPluralSingular} created`,
          message: `${createdInvitations.length} ${createdPluralSingular} was successfully created`,
          variant: 'success',
        });
      }

      if (blockedInvitations.length) {
        const blockedInvitationsByChecklistError = {
          message: blockedInvitations[0].reason,
          data: { code: 'INVALID_NOT_PERMITTED', length: blockedInvitations.length },
        };

        throw blockedInvitationsByChecklistError;
      }

      onClose();
    },
    onError: (error: any) => {
      let heading = `Error creating ${createdPluralSingular}`;
      let message = parseServerError(error);
      let shouldCloseModal = true;

      const _blockedInvitationsPluralSingular = formatSingularPlural(
        error.data?.length,
        'invitation',
        'invitations',
        true,
      );

      switch (error.data?.code) {
        case 'INVALID_INVITATION':
          error.customMsg = `Your invitation start and end date exceeds the 5 year limit. Please adjust the ${createdPluralSingular} to fit within this limit and try again.`;
          shouldCloseModal = false;
          break;

        case 'INVALID_NOT_PERMITTED':
          heading = `Blocked ${_blockedInvitationsPluralSingular}`;
          message = `${error.data.length} ${_blockedInvitationsPluralSingular} blocked. Check your email for details`;
          shouldCloseModal = true;
          break;

        default:
          shouldCloseModal = false;
          break;
      }

      addNotification({
        heading,
        message,
        variant: 'danger',
      });

      shouldCloseModal && onClose();
    },
  });

  return { createInvitations, isLoadingCreate };
}
